import axios from "../../../../../api/axios";
import { toast } from 'react-toastify';

/**
 * API endpoints for Move-In/Out Inspection operations
 * Updated to match the new backend routes (January 2026 version)
 */
export const ENDPOINTS = {
  BASE: "/v2/move-in-out-inspections",
  CREATE: "/v2/move-in-out-inspections",
  GET_ALL: "/v2/move-in-out-inspections",
  GET_BY_ID: (id) => `/v2/move-in-out-inspections/${id}`,
  UPDATE: (id) => `/v2/move-in-out-inspections/${id}`,
  DELETE: (id) => `/v2/move-in-out-inspections/${id}`,
  SEND_REPORT: (id) => `/v2/move-in-out-inspections/${id}/send-report`,
  DOWNLOAD_BUNDLE: (id) => `/v2/move-in-out-inspections/${id}/download-bundle`,

  REQUEST_ATTACHMENT_UPLOAD: (id) => `/v2/move-in-out-inspections/${id}/attachments/request`,
  CONFIRM_ATTACHMENT_UPLOAD: (id) => `/v2/move-in-out-inspections/${id}/attachments/confirm`,

  GET_SIGNED_URLS: "/v2/move-in-out-inspections/signed-urls",
  GET_TENANTS: "/v2/move-in-out-inspections/tenants",
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Fallback message
 */
const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage;
  console.error(defaultMessage, error);
  throw new Error(message);
};

/**
 * Create a new Move-In/Out Inspection
 * @param {Object} payload - Full inspection object
 * @returns {Promise<Object>} Created inspection data
 */
export const createMoveInOutInspection = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE, payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to create move-in/out inspection");
  }
};

/**
 * Get all Move-In/Out Inspections (with optional filters)
 * @param {Object} [params={}] - Query params: propertyId, unit_number, month, year
 * @returns {Promise<Object>} { success, count, data, message? }
 */
export const getMoveInOutInspectionList = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_ALL, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inspections");
    return { success: false, data: [], count: 0 };
  }
};

/**
 * Get single inspection by ID
 * @param {string} id - Inspection ID
 * @returns {Promise<Object>} Inspection details
 */
export const getMoveInOutInspectionById = async (id) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_BY_ID(id));
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inspection details");
  }
};

/**
 * Update an existing inspection
 * @param {string} id - Inspection ID
 * @param {Object} payload - Fields to update
 * @returns {Promise<Object>} Updated inspection
 */
export const updateMoveInOutInspection = async (id, payload) => {
  try {
    const response = await axios.patch(ENDPOINTS.UPDATE(id), payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to update inspection");
  }
};

/**
 * Delete an inspection (hard delete)
 * @param {string} id - Inspection ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteMoveInOutInspection = async (id) => {
  try {
    const response = await axios.delete(ENDPOINTS.DELETE(id));
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete inspection");
  }
};

/**
 * Send Move-In/Out inspection report via email
 * @param {string} id - Inspection ID
 * @param {Object} [options={}] - Optional configuration
 * @param {string[]} [options.emails] - Custom list of email addresses (if not provided → sends to defaults: company, managers, lead staff, tenants)
 * @returns {Promise<Object>} Response containing sentTo list, filename, etc.
 */
export const sendMoveInOutInspectionReport = async (id, options = {}) => {
  const { emails } = options;

  try {
    const payload = {};
    if (Array.isArray(emails) && emails.length > 0) {
      payload.emails = emails.filter(email => typeof email === 'string' && email.trim() && email.includes('@'));
    }

    const response = await axios.post(ENDPOINTS.SEND_REPORT(id), payload);
    const { message, sentTo } = response.data;
    toast.success(message || `Report sent successfully to ${sentTo?.length || 0} recipient(s)`);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to send inspection report");
  }
};

/**
 * Request presigned URL to upload photo/video to specific inspection item
 * @param {string} id - Inspection ID
 * @param {Object} payload - { itemIndex: number, phase: 'move_in'|'move_out', fileName: string, contentType: string }
 * @returns {Promise<Object>} { presignedUrl, key, expectedContentType, expiresIn }
 */
export const requestAttachmentUpload = async (id, payload) => {
  try {
    const response = await axios.post(ENDPOINTS.REQUEST_ATTACHMENT_UPLOAD(id), payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to get upload URL");
  }
};

/**
 * Confirm that file was uploaded successfully
 * @param {string} id - Inspection ID
 * @param {Object} payload - { itemIndex: number, phase: 'move_in'|'move_out', key: string }
 * @returns {Promise<Object>} Updated attachments info
 */
export const confirmAttachmentUpload = async (id, payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CONFIRM_ATTACHMENT_UPLOAD(id), payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to confirm upload");
  }
};

/**
 * Get multiple signed URLs for viewing attachments
 * @param {string[]} keys - Array of S3 object keys
 * @returns {Promise<Record<string, string|null>>} { key: signedUrl | null }
 */
export const getMultipleSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};

  try {
    const response = await axios.post(ENDPOINTS.GET_SIGNED_URLS, { keys });
    return response.data.data || {};
  } catch (error) {
    handleApiError(error, "Failed to generate signed URLs");
    return {};
  }
};

/**
 * Get tenant details for a specific unit
 * @param {string} propertyId
 * @param {string} buildingName
 * @param {string} unitNumber
 * @returns {Promise<Object>} { success, data: { tenants } }
 */
export const getTenantDetails = async (propertyId, buildingName = "", unitNumber) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_TENANTS, {
      params: { propertyId, buildingName, unitNumber },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch tenant details");
    return { success: false, data: { tenants: [] } };
  }
};

/** Download the report PDF and category-organized media as one ZIP file. */
export const downloadMoveInOutInspectionBundle = async (id) => {
  try {
    const response = await axios.get(ENDPOINTS.DOWNLOAD_BUNDLE(id), { responseType: 'blob' });
    const contentDisposition = response.headers?.['content-disposition'] || '';
    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch?.[1] || 'MoveInOut_Inspection.zip';
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleApiError(error, 'Failed to download inspection bundle');
  }
};
